/**
 * THE INTERNAL / EXTERNAL BOUNDARY — the one place it is written down.
 *
 * Brian, 2026-09-11: *"one must be internal and the other is external — team
 * meeting room being for coaches. customer journey should be renamed to
 * something about INTERNAL MANUFACTURING."*
 *
 * And on what the external one is for: *"TEAM MEETING ROOM is so we can
 * communicate and get approvals for a customer. this is where we get all
 * signatures, we get approvals, rosters, time stamped, we internally have this
 * linked to reviews so we can ask for a review, we can push the marketing
 * material to a point of contact from our marketing team, we can provide
 * assets for delivery events, we can push a contract in front here when they
 * are happy."*
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL.
 *
 * The two surfaces converged until a rep could not tell which to open. The
 * measured cause (pass 1, 2026-09-11) was not that either screen was wrong —
 * it was that the boundary lived in nobody's code. `nav.js` carried one answer
 * in a comment, the Team Room carried another in its own repo's header, and
 * CLAUDE.md did not mention the Team Room at all. Three places, no agreement,
 * and the only surface that stated a purpose out loud stated a different one
 * from the surface next to it.
 *
 * So the rule is a CONSTANT, imported by every screen that renders it. Two
 * screens explaining themselves from one string cannot drift into explaining
 * themselves differently — which is precisely what happened to the prose they
 * are replacing.
 *
 * ⚠️ EXTERNAL MEANS CUSTOMER-VISIBLE, NOT STAFF-FREE. The Team Meeting Room
 * keeps its staff console (`team.srchamp.com/staff`) — that is the SR side of
 * running an external record, and roughly twenty panels of it. Reading
 * "external" as "no SR staff touch it" is the misreading that would undo this
 * whole boundary, so it is stated here rather than left to be inferred.
 */

/* ---- THE NAME -------------------------------------------------------------
 *
 * Brian, 2026-09-11: **PRODUCTION.** It is the vocabulary the 14-stage tracker
 * inside this screen has always used, so the page and its own contents finally
 * agree — "Customer Journey" was the last thing on the screen still calling the
 * work by a customer-facing name.
 *
 * ⚠️ EVERY OTHER FILE READS THESE. `nav.js`, `App.jsx`, the page header, the
 * purpose strip and both cross-links, so the name lives in one place and a
 * future rename is two string edits and no hunt.
 */
export const INTERNAL_LABEL = 'Production'
export const INTERNAL_ROUTE = '/production'

/**
 * The old route, kept alive forever by a redirect in `App.jsx`.
 *
 * ⚠️ DO NOT DELETE IT WHEN IT LOOKS UNUSED. Every bookmark, every link pasted
 * into a message or an email, every deep link in a prior dispatch points at
 * `/customer-journey`.
 * A rename that 404s the old URL is how a team concludes the new thing is
 * broken — and they conclude it on the first click, before ever seeing the
 * screen that works.
 */
export const INTERNAL_LEGACY_ROUTE = '/customer-journey'

export const EXTERNAL_LABEL = 'Team Meeting Rooms'
export const EXTERNAL_ROUTE = '/team-rooms'
export const EXTERNAL_URL = 'https://team.srchamp.com'
export const EXTERNAL_STAFF_URL = 'https://team.srchamp.com/staff'

/** The boundary in one sentence, rendered verbatim on both surfaces. */
export const BOUNDARY =
  'One rule decides which to open: if the customer can see it, it lives in the ' +
  'Team Meeting Room. If it is how SR builds the piece, it lives here.'

/**
 * ⚠️ "WHAT THEY HAVE ORDERED" — THE ONE OVERLAP THAT IS NOT DRIFT, AND THE
 * CORRECTION THAT MAKES IT SAFE.
 *
 * Brian added the Room *"also shows what they have ordered"* and set the rule
 * that ClickUp owns the order and both surfaces read it.
 *
 * **The first half is right; the second half is not what is happening, and the
 * difference is the reason this is safe rather than a tenth duplicate.**
 * Measured 2026-09-11 in the portal's own `lifecycle.js`:
 *
 *   if (account.shopifyTag || (account.shopifyKeys || []).length) …
 *
 * The Room's order list comes from **SHOPIFY team-store orders**, matched by
 * tag. It never reads ClickUp. So the two surfaces are not two readings of one
 * record — they are **two different objects that share a word**:
 *
 *   ClickUp 901000306889   the PRODUCTION ORDER — one task per team order,
 *                          3,467 of them. Quantity, status, assignee, the
 *                          floor's work. What Production reads.
 *   Shopify team store     the ATHLETE PURCHASES — which individual bought,
 *                          their size, their line item. What the coach means
 *                          by "who has ordered", and what the Room shows.
 *
 * A coach asking the question means *"which of my players have bought yet"*.
 * The floor asking it means *"what are we making"*. Same four words, different
 * questions, different systems, and each surface answers the one its audience
 * is asking. Neither maintains the other's copy, so there is nothing to drift.
 *
 * ⚠️ WHERE IT COULD STILL BITE — REPORTED, NOT DESIGNED OVER. Both produce a
 * COUNT of rings, from unrelated systems, and nothing reconciles them. A
 * ClickUp order for 40 against 31 athletes checked out in Shopify is a real and
 * useful discrepancy — it is the gap a rep should be chasing — but today no
 * screen puts the two numbers side by side, so nobody sees it. See
 * docs/BACKEND-HANDOVER.md §11.6.
 *
 * This constant exists so neither purpose strip ever claims orders "live here".
 * Both show orders. They show different ones, to different people.
 */
export const ORDERS_NOTE = Object.freeze({
  internal:
    'Shows the production order — quantity, status and the floor’s queue, from ClickUp.',
  external:
    'Shows the team’s own purchases — which athletes have bought, and their sizes, from the team store.',
})

/**
 * What each surface IS, what it is NOT, and where the other one lives.
 *
 * `isNot` is the field that does the work. "What this is" alone never stopped
 * two screens overlapping — a rep reads a purpose line, it sounds plausible,
 * and they carry on using whichever they opened first. Naming the thing a
 * surface deliberately does NOT do is what sends them to the other one.
 */
export const SURFACES = Object.freeze({
  internal: Object.freeze({
    key: 'internal',
    audience: 'Internal',
    label: INTERNAL_LABEL,
    route: INTERNAL_ROUTE,
    is: 'The floor’s view of the work — the 14 production stages, the internal queue, which orders are blocked and how late they are. For SR.',
    orders: ORDERS_NOTE.internal,
    isNot: 'No customer ever sees this screen, and nothing here is evidence of anything. Approvals, signatures, rosters and proofs are NOT recorded here.',
    otherLabel: EXTERNAL_LABEL,
    otherWhere: 'Anything the customer signs, sees or is sent lives in the Team Meeting Room.',
    otherHref: EXTERNAL_ROUTE,
    otherExternal: false,
  }),
  external: Object.freeze({
    key: 'external',
    audience: 'Customer-facing',
    label: EXTERNAL_LABEL,
    route: EXTERNAL_ROUTE,
    is: 'SR’s ONE external surface — communication, approvals, signatures, rosters and timestamps, the review ask, marketing material, delivery-event assets, and the contract when they are happy. For the coach.',
    orders: ORDERS_NOTE.external,
    isNot: 'Not a second copy of the floor. It shows a coach where their rings are in language they can act on; it does not run SR’s production queue.',
    otherLabel: INTERNAL_LABEL,
    otherWhere: `The floor’s own view of the work — stages, queue and what is late — lives in ${INTERNAL_LABEL}.`,
    otherHref: INTERNAL_ROUTE,
    otherExternal: false,
  }),
})

/**
 * THE PER-ACCOUNT CROSS-LINK, and the honest limit on it.
 *
 * The portal DOES have a per-account room at `/portal?account=<id>` — read
 * off its own staff console on 2026-09-11. But that `<id>` is the PORTAL's
 * account id, and nothing on an internal order record carries it: the internal
 * view knows a ClickUp task id and a school name, and the portal knows its own
 * ids. There is no mapping between them on either side today.
 *
 * ⚠️ SO THIS RETURNS THE CONSOLE, NOT A GUESSED DEEP LINK. Composing
 * `/portal?account=<clickup id>` would produce a URL that resolves to nothing,
 * or worse, to somebody else's room. A link that lands a rep on the console
 * with the account name to search for costs them one search; a link that opens
 * the wrong team's record costs a great deal more. `/staff` takes no query
 * parameters at all (verified — it reads none), so the search cannot be
 * pre-filled either.
 *
 * Closing this is a portal-side change, written up in docs/BACKEND-HANDOVER.md:
 * accept `?q=` on /staff, or key portal accounts by `sr_school_key` so the two
 * systems can name the same account.
 */
export function roomLinkFor(/* order */) {
  return { href: EXTERNAL_STAFF_URL, deepLinked: false }
}

/* ---- ONE VOCABULARY ACROSS THE BOUNDARY ----------------------------------
 *
 * The two surfaces name the same axis differently, measured 2026-09-11:
 *
 *   internal (lib/journey.js JOURNEY_PHASES)
 *     new · approvals · production · delivery · complete   (+ hold, unmapped)
 *   external (team.srchamp.com/lifecycle.js LIFECYCLE)
 *     setup · ordering · approvals · production · delivered · closed
 *
 * Neither derives from the other, so the same order can read `complete` on one
 * screen and `delivered` on the other and both be "right". `ordering` — the
 * window where athletes are still buying — has no internal equivalent at all,
 * and `complete` collapses a distinction the portal keeps.
 *
 * ⚠️ THIS MAP IS THE AGREED TRANSLATION, NOT A SYNC. It writes nothing and it
 * is deliberately NOT total: `ordering` maps to null, because inventing an
 * internal phase for it would put a made-up state on an order. A null here
 * means "the internal model has no word for this yet" — which is a finding to
 * report, not a gap to paper over.
 *
 * The direction follows the rule: the Room maintains its lifecycle (it is the
 * customer-visible one), the internal view reads it. When a read route exists
 * this is the only place that has to agree.
 */
export const LIFECYCLE_TO_PHASE = Object.freeze({
  setup: 'new',
  ordering: null,
  approvals: 'approvals',
  production: 'production',
  delivered: 'delivery',
  closed: 'complete',
})

/** The portal's lifecycle key → an internal phase, or null when none exists. */
export function phaseForLifecycle(lifecycle) {
  const k = String(lifecycle || '').trim().toLowerCase()
  return Object.prototype.hasOwnProperty.call(LIFECYCLE_TO_PHASE, k)
    ? LIFECYCLE_TO_PHASE[k]
    : undefined // unknown word — never guess a phase from one
}
